import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../config/api';
import { FaArrowLeft, FaHistory } from 'react-icons/fa';
import './AdminPages.css';
import './AdminEmailHistory.css';

const KIND_LABELS = {
  '': '(tous)',
  magic_link: 'Lien magique',
  password_reset: 'Réinitialisation mot de passe',
  bulk: 'Envoi groupé (membres)',
  registration_pending: 'Inscription en attente',
  membership_validated: 'Adhésion confirmée',
  contact_form: 'Formulaire contact',
  ticket_order_user: 'Demande billet (membre)',
  ticket_order_admin: 'Demande billet (admin)',
  ticket_order_confirmed: 'Confirmation billet (membre)',
  ticket_order_confirmed_admin: 'Confirmation billet (admin)',
  ticket_order_sector_change: 'Modification secteur/prix billet',
  other: 'Autre',
};

export default function AdminEmailHistory() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [filterDraft, setFilterDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (appliedSearch.trim()) params.set('q', appliedSearch.trim());
      if (kindFilter) params.set('kind', kindFilter);
      const res = await api.get(`/email/send-history?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total ?? 0);
      setPages(res.data.pages ?? 1);
    } catch (e) {
      console.error(e);
      alert('Erreur lors du chargement de l\'historique des e-mails');
    } finally {
      setLoading(false);
    }
  }, [page, limit, appliedSearch, kindFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const applyFilters = () => {
    setPage(1);
    setAppliedSearch(filterDraft);
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('fr-CH', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-container-inner">
        <div className="admin-page-header">
          <div>
            <Link to="/admin" className="email-history-back">
              <FaArrowLeft aria-hidden /> Retour au tableau de bord
            </Link>
            <h1>
              <FaHistory style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Historique des e-mails envoyés
            </h1>
            <p className="email-history-intro">
              Chaque ligne indique le <strong>e-mail destinataire</strong> et le{' '}
              <strong>titre</strong> (sujet) du message envoyé par le site.
            </p>
          </div>
        </div>

        <div className="email-history-toolbar">
          <label className="email-history-field">
            <span>Filtrer (e-mail ou sujet)</span>
            <div className="email-history-inline">
              <input
                type="search"
                className="email-history-input"
                value={filterDraft}
                onChange={(e) => setFilterDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
                placeholder="Fragment d’adresse ou de sujet…"
              />
              <button type="button" className="btn-add" onClick={applyFilters}>
                Appliquer
              </button>
            </div>
          </label>
          <label className="email-history-field">
            <span>Type d’envoi</span>
            <select
              className="email-history-select"
              value={kindFilter}
              onChange={(e) => {
                setKindFilter(e.target.value);
                setPage(1);
              }}
            >
              {Object.entries(KIND_LABELS).map(([value, label]) => (
                <option key={value || '__all'} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="email-history-field">
            <span>Lignes par page</span>
            <select
              className="email-history-select"
              value={String(limit)}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="email-history-stats">
          {loading ? (
            <span>Chargement…</span>
          ) : (
            <span>
              {total} entrée{total !== 1 ? 's' : ''} au total · page {page} / {pages}
            </span>
          )}
        </div>

        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Date et heure</th>
                <th>Destinataire</th>
                <th>Titre (sujet)</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {loading && logs.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                    Chargement…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Aucune entrée. Les envois effectués avant l’activation de cet historique n’apparaissent pas
                    ici.
                  </td>
                </tr>
              ) : (
                logs.map((row) => (
                  <tr key={`${row._id}`}>
                    <td className="email-history-cell-nowrap">{fmtDate(row.sentAt)}</td>
                    <td>
                      <a href={`mailto:${row.recipientEmail}`}>{row.recipientEmail}</a>
                    </td>
                    <td className="email-history-subject" title={row.subject}>
                      {row.subject}
                    </td>
                    <td>{KIND_LABELS[row.kind] || row.kind || 'Autre'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="email-history-pagination">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              Précédent
            </button>
            <span>
              Page {page} sur {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </button>
          </div>
        )}

        <p className="email-history-note">
          L’historique est enregistré à partir du déploiement de cette fonctionnalité&nbsp;: seuls les e-mails envoyés ensuite apparaîtront.
        </p>
      </div>
    </div>
  );
}
